import InstallPrompt from '@/views/_partials/InstallPrompt';
import RefreshServiceWorker from '@/views/_partials/RefreshServiceWorker';
import 'flag-icons/css/flag-icons.min.css';
import { createPinia } from 'pinia';
import { createApp, watch } from 'vue';
import App from './App.vue';
import { useYupExtensions } from './helpers/yupExtensions';
import i18n from './i18n';
import './registerServiceWorker';
import router from './router';
import { useStore } from './store';
import BackButton from './views/_partials/BackButton';
import DotsBackground from './views/_partials/DotsBackground';
import PlayButton from './views/_partials/PlayButton';
import ProductCatLinks from './views/_partials/ProductCatLinks';
import ProductDetails from './views/_partials/ProductDetails';
import ProductLines from './views/_partials/ProductLines';
import TouchIcon from './views/_partials/TouchIcon';
import VideoModal from './views/_partials/VideoModal';

useYupExtensions();

const app = createApp(App);
app.use(createPinia());
const store = useStore();
watch(() => store.settings.language, language => {
	i18n.global.locale.value = language;
}, { immediate: true });
store.initializeSettings().then(() => {
	// Set i18n locale from store
	i18n.global.locale.value = store.settings.language;
	app.use(i18n);
	app.use(router);
	app.mount('#app');
});

// Prime a shared PWA compatibility snapshot at app start
store.refreshPwaCompat();

app.component('BackButton', BackButton);
app.component('DotsBackground', DotsBackground);
app.component('InstallPrompt', InstallPrompt);
app.component('PlayButton', PlayButton);
app.component('ProductCatLinks', ProductCatLinks);
app.component('ProductDetails', ProductDetails);
app.component('ProductLines', ProductLines);
app.component('RefreshServiceWorker', RefreshServiceWorker);
app.component('TouchIcon', TouchIcon);
app.component('VideoModal', VideoModal);

// refresh settings when updated
if ('BroadcastChannel' in window) {
	const broadcast = new BroadcastChannel('settings');
	broadcast.addEventListener('message', event => {
		if (event.data === 'update') {
			store.refreshSettings();
		}
	});
}

window.addEventListener('beforeinstallprompt', (e) => {
	e.preventDefault();
	store.setPwaPrompt?.(e) || (store.installPrompt = e);
	store.setCustomPwaBanner(localStorage.getItem('pwaUserChoice') != 'no');
});

window.addEventListener('appinstalled', () => {
	store.showInstallBanner = false;
	store.installPrompt = null;
});

// Keep SW status in sync via SW → client messages and snapshot
if ('serviceWorker' in navigator) {
	navigator.serviceWorker.ready
		.then((registration) => {
			registration.active?.postMessage({ type: 'PING' });
			if (navigator.onLine) registration.active?.postMessage({ type: 'CACHE_VIDEOS' });
			store.swStatus = {
				...store.swStatus,
				registered: true,
				ready: true
			};
		})
		.catch(() => { /* ignore */ });

	navigator.serviceWorker.addEventListener('message', (event) => {
		const { type, payload } = event.data || {};
		switch (type) {
			case 'APP_CACHE_PROGRESS':
				store.swStatus = { ...store.swStatus, appDownload: payload, updating: payload.downloading };
				if (payload.error) store.swStatus = { ...store.swStatus, error: true, errorMsg: payload.error };
				break;
			case 'VIDEO_CACHE_STATUS':
				store.swStatus = { ...store.swStatus, videoCache: payload };
				break;
			case 'SW_READY':
				store.swStatus = { ...store.swStatus, registered: true, ready: true };
				break;
			case 'SW_UPDATE_AVAILABLE':
				store.serviceWorkerUpdate = true;
				store.swStatus = { ...store.swStatus, updateAvailable: true, updating: false };
				break;
			case 'SW_UPDATED':
				store.serviceWorkerUpdate = false;
				store.swStatus = { ...store.swStatus, updateAvailable: false, updating: false };
				break;
			case 'STATUS_SNAPSHOT':
				store.swStatus = { ...store.swStatus, ready: !!payload?.ready, updateAvailable: !!payload?.updateAvailable, contentCached: !!payload?.contentCached };
				break;
		}
	});

	navigator.serviceWorker.addEventListener('controllerchange', () => {
		navigator.serviceWorker.controller?.postMessage({ type: 'PING' });
		if (navigator.onLine) navigator.serviceWorker.controller?.postMessage({ type: 'CACHE_VIDEOS' });
	});
}

// Keep offline status in sync globally
store.swStatus = { ...store.swStatus, isOffline: !navigator.onLine };
window.addEventListener('online', () => {
	store.swStatus = { ...store.swStatus, isOffline: false };
	navigator.serviceWorker?.controller?.postMessage({ type: 'CACHE_VIDEOS' });
});
window.addEventListener('offline', () => { store.swStatus = { ...store.swStatus, isOffline: true }; });

